import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from '@monorepo/site-settings';
import { useI18n } from '@monorepo/i18n';
import { Icon } from '@monorepo/icons';
import { Button, BUTTON_INTENT } from '@monorepo/ui-kit';
import useGetPersonalOfficeMenu from '../../hooks/useGetPersonalMenu';

const UserScrollMenu = () => {
  const items = useGetPersonalOfficeMenu();
  const { t } = useI18n();
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const location = useLocation();
  const { pathname } = location;
  const { generalRoutes } = useSiteSettings();
  const { personalOffice } = generalRoutes;

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [items]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 300;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const activeItem = items.find((item) => pathname.includes(item.link));
  const inactiveItems = items.filter((item) => !pathname.includes(item.link));

  return (
    <div className="flex items-center gap-1 border-b border-gray-300 py-2">
      {activeItem && (
        <div className="shrink-0 ml-2">
          <Link
            to={`/${personalOffice}/${activeItem.link}`}
            className="flex items-center rounded px-4 py-2 whitespace-nowrap mb-0.5 shadow-sm bg-gray-800 text-gray-100"
          >
            {t(activeItem.title)}
          </Link>
        </div>
      )}

      <div className="shrink-0 w-10">
        {canScrollLeft && (
          <Button
            onClick={() => scroll('left')}
            intent={BUTTON_INTENT.tertiary}
            className="hover:bg-transparent"
            isOnlyIcon
          >
            <Icon name="arrow_left" />
          </Button>
        )}
      </div>

      <ul
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-2 px-1 flex-1 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {inactiveItems.map((item) => (
          <li key={item.title} className="list-none">
            <Link
              to={`/${personalOffice}/${item.link}`}
              className="flex items-center rounded px-4 py-2 whitespace-nowrap mb-0.5 shadow-sm bg-gray-100 text-gray-700 border border-gray-300"
            >
              {t(item.title)}
            </Link>
          </li>
        ))}
      </ul>

      <div className="shrink-0 w-10">
        {canScrollRight && (
          <Button
            onClick={() => scroll('right')}
            intent={BUTTON_INTENT.tertiary}
            className="hover:bg-transparent"
            isOnlyIcon
          >
            <Icon name="arrow_right" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default UserScrollMenu;
